// BUILD-042 Slice A UAT correction (2026-10-02) — split a term-history list into current /
// upcoming / history.
//
// hr-data.ts's termHistory()/currentTerm() both filter on `superseded_by_term_id IS NULL`, but
// nothing in this codebase ever SETS that column (createFinalizedEmploymentChange never touches
// it) — so termHistory() actually returns EVERY term row ever created for the Employee, past,
// current, and future-dated "upcoming" alike, ordered effective_date DESC. The data already
// contains everything decision requirement #3 asks for ("current/upcoming terms, preserved
// history"); the UI previously just assumed termHistory[0] was "current" without checking
// whether its effective_date was actually <= today, which would mis-render a future-dated
// upcoming term as the current one.
//
// This is a pure display-layer split: it does not mutate or re-request anything, and it is
// deliberately tolerant of more than one future-dated row (reports every row with
// effectiveDate > asOf as "upcoming", in effective_date order) and more than one past-or-today
// row (the most recent past-or-today row is "current"; the rest remain in "history").

export interface TermLike {
  effectiveDate: string;
  [key: string]: unknown;
}

export interface SplitTermHistory<T extends TermLike> {
  current: T | null;
  upcoming: T[];
  history: T[];
}

/** `asOf` defaults to the browser's local today (YYYY-MM-DD) when omitted. */
export function splitTermHistory<T extends TermLike>(termHistory: T[], asOf?: string): SplitTermHistory<T> {
  const today = asOf || new Date().toISOString().slice(0, 10);
  // termHistory arrives effective_date DESC (hr-data.ts's own ORDER BY) — do not assume that
  // ordering here beyond what is needed; sort defensively so a differently-ordered input (e.g.
  // a future refactor, or a test fixture) cannot silently misclassify current vs. history.
  const sorted = [...termHistory].sort((a, b) => (a.effectiveDate < b.effectiveDate ? 1 : a.effectiveDate > b.effectiveDate ? -1 : 0));

  const upcoming = sorted.filter((t) => t.effectiveDate > today);
  const pastOrToday = sorted.filter((t) => t.effectiveDate <= today);
  const current = pastOrToday.length > 0 ? pastOrToday[0] : null;
  const history = pastOrToday.slice(1);
  // Upcoming terms are naturally soonest-first when sorted DESC-then-filtered; present them
  // soonest-first (ascending by date) since that is the order a reader expects for "what's next".
  upcoming.reverse();

  return { current, upcoming, history };
}
